import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ColumnType, SortOrder } from "antd/es/table/interface";
import { DownloadOutlined } from "@ant-design/icons";

import QueryTable from "@/components_v2/Tables/QueryTable";
import type { QueryTableSnapshot } from "@/components_v2/Tables/types";
import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import BaseProgressBar from "@/components_v2/ProgressBars/BaseProgressBar";
import BaseStatusChip from "@/components_v2/StatusChip/BaseStatusChip";
import { STATUS_CHIP_VARIANTS } from "@/components_v2/StatusChip/constants";
import { useLazyGetParticipantsQuery } from "@/redux/service";
import { useSessionState } from "@/shared/hooks/useSessionState";
import { userProfilePath } from "@/domains/users/routes";
import type { Participant } from "@/redux/certifications/types";
import PathTag from "@/domains/certifications/components/PathTag";
import StatusChip from "@/domains/certifications/components/StatusChip";
import { countTrackRequirementsOfType } from "@/domains/certifications/progress";
import {
  deriveParticipantStatus,
  getBanState,
  getPathCredential,
} from "@/domains/certifications/status";
import { formatCertificationDate } from "@/domains/certifications/utils";
import ParticipantActionsCell from "@/features/certifications/participants/components/ParticipantActionsCell";
import ParticipantFilters from "@/features/certifications/participants/components/ParticipantFilters";
import { useParticipantExport } from "@/features/certifications/participants/hooks/useParticipantExport";
import {
  DEFAULT_TABLE_STATE,
  STATUS_FILTERS,
} from "@/features/certifications/participants/constants";
import {
  ParticipantFiltersState,
  ParticipantsTableState,
  ParticipantsTabProps,
} from "@/features/certifications/participants/types";
import { BAN_KINDS } from "@/domains/certifications/constants";

import "./styles.css";

const WEEKS_BAR_HEIGHT = 6;

const getSortOrder = (
  ordering: string | undefined,
  field: string,
): SortOrder | undefined => {
  if (ordering === field) {
    return "ascend";
  }
  return ordering === `-${field}` ? "descend" : undefined;
};

const ParticipantsTab = ({ certification }: ParticipantsTabProps) => {
  const { t } = useTranslation();
  const [tableState, setTableState] = useSessionState<ParticipantsTableState>(
    `certification-participants-${certification.slug}`,
    DEFAULT_TABLE_STATE,
  );
  const [initialState] = useState(tableState);
  const { filters } = tableState;

  const setFilters = useCallback(
    (next: ParticipantFiltersState) =>
      setTableState((prev) => ({ ...prev, filters: next })),
    [setTableState],
  );
  const saveTableState = useCallback(
    (snapshot: QueryTableSnapshot) =>
      setTableState((prev) => ({ ...prev, ...snapshot })),
    [setTableState],
  );

  const queryFilters = useMemo(() => {
    const { status, ...rest } = filters;
    return {
      ...rest,
      ...(status ? STATUS_FILTERS[status] : {}),
      certificationSlug: certification.slug,
    };
  }, [filters, certification.slug]);

  const { handleExport, isExporting } = useParticipantExport({
    certificationSlug: certification.slug,
    filters: queryFilters,
  });

  const weekCount = certification.modules.length;
  const checkCountByTrack = useMemo(
    () =>
      new Map(
        certification.tracks.map((track) => [
          track.id,
          countTrackRequirementsOfType(
            certification,
            track.id,
            "KNOWLEDGE_CHECK_PASS",
          ),
        ]),
      ),
    [certification],
  );

  const columns: ColumnType<Participant>[] = useMemo(
    () => [
      {
        key: "participant",
        dataIndex: "name",
        sorter: true,
        defaultSortOrder: getSortOrder(initialState.ordering, "name"),
        title: t("certification.admin.participants.columns.participant"),
        render: (_, participant) => (
          <Link
            to={userProfilePath(participant.user.id)}
            className="participants-profile-link flex flex-col"
          >
            <span className="participants-name body-s-regular">
              {participant.user.fullName}
            </span>

            <span className="participants-email body-xs-regular">
              {participant.user.email}
            </span>
          </Link>
        ),
      },
      {
        key: "path",
        title: t("certification.admin.participants.columns.path"),
        render: (_, participant) => (
          <PathTag
            certification={certification}
            trackId={participant.selectedTrack}
          />
        ),
      },
      {
        key: "weeksCompleted",
        dataIndex: "progress",
        sorter: true,
        defaultSortOrder: getSortOrder(initialState.ordering, "progress"),
        title: t("certification.admin.participants.columns.weeksCompleted"),
        render: (_, { weeksCompleted }) => {
          const isComplete = weekCount > 0 && weeksCompleted >= weekCount;
          const label = t("certification.admin.participants.outOf", {
            count: weeksCompleted,
            total: weekCount,
          });

          return (
            <div className="flex items-center gap-200">
              <div className="participants-weeks-bar">
                <BaseProgressBar
                  value={weeksCompleted}
                  total={Math.max(weekCount, 1)}
                  strokeWidth={WEEKS_BAR_HEIGHT}
                  strokeColor={
                    isComplete ? "var(--primary-1000)" : "var(--primary-600)"
                  }
                  aria-label={t(
                    "certification.admin.participants.columns.weeksCompleted",
                  )}
                  aria-valuetext={label}
                />
              </div>

              <span className="body-s-regular">{label}</span>
            </div>
          );
        },
      },
      {
        key: "knowledgeChecks",
        align: "center",
        title: t("certification.admin.participants.columns.knowledgeChecks"),
        render: (_, { knowledgeChecksPassed, selectedTrack }) => {
          const total = checkCountByTrack.get(selectedTrack) ?? 0;
          return (
            <span
              className={`body-s-regular ${
                total > 0 && knowledgeChecksPassed >= total
                  ? "participants-complete"
                  : ""
              }`}
            >
              {t("certification.admin.participants.outOf", {
                count: knowledgeChecksPassed,
                total,
              })}
            </span>
          );
        },
      },
      {
        key: "discussionPosts",
        align: "center",
        title: t("certification.admin.participants.columns.discussionPosts"),
        render: (_, { discussionsPosted }) => (
          <span
            className={`body-s-regular ${
              discussionsPosted === 0 ? "participants-email" : ""
            }`}
          >
            {discussionsPosted}
          </span>
        ),
      },
      {
        key: "status",
        title: t("certification.admin.participants.columns.status"),
        render: (_, participant) => (
          <StatusChip
            status={deriveParticipantStatus(participant, weekCount)}
          />
        ),
      },
      {
        key: "banned",
        title: t("certification.admin.participants.columns.banned"),
        render: (_, participant) => {
          const ban = getBanState(participant);
          if (!ban) {
            return (
              <BaseStatusChip
                label={t("certification.admin.participants.ban.notBanned")}
                variant={STATUS_CHIP_VARIANTS.NEUTRAL}
              />
            );
          }

          return (
            <div className="flex flex-col items-start gap-50">
              <BaseStatusChip
                label={t("certification.admin.participants.ban.banned")}
                variant={STATUS_CHIP_VARIANTS.RED}
              />

              <span className="participants-banned body-xs-regular">
                {ban.kind === BAN_KINDS.TIMED
                  ? t("certification.admin.participants.ban.until", {
                      date: formatCertificationDate(ban.endsAt),
                    })
                  : t("certification.admin.participants.ban.permanent")}
              </span>
            </div>
          );
        },
      },
      {
        key: "certified",
        title: t("certification.admin.participants.columns.certified"),
        render: (_, participant) => {
          const issuedAt = getPathCredential(participant)?.issuedAt;
          return issuedAt ? (
            <span className="body-s-regular">
              {formatCertificationDate(issuedAt)}
            </span>
          ) : (
            <span aria-hidden>—</span>
          );
        },
      },
      {
        key: "actions",
        title: t("certification.admin.participants.columns.actions"),
        render: (_, participant) => (
          <ParticipantActionsCell
            certification={certification}
            participant={participant}
          />
        ),
      },
    ],
    [certification, checkCountByTrack, initialState.ordering, t, weekCount],
  );

  return (
    <div className="table-container-layout">
      <div className="participants-toolbar flex flex-wrap items-center gap-300">
        <ParticipantFilters
          certification={certification}
          initialFilters={initialState.filters}
          setFilters={setFilters}
        />

        <BaseButton
          variant={BUTTON_VARIANTS.SECONDARY}
          size={BUTTON_SIZES.SMALL}
          leadingIcon={<DownloadOutlined />}
          loading={isExporting}
          onClick={handleExport}
        >
          {t("certification.admin.participants.export")}
        </BaseButton>
      </div>

      <QueryTable<Participant>
        query={useLazyGetParticipantsQuery}
        filters={queryFilters}
        columns={columns}
        pagination={{
          defaultCurrent: initialState.page,
          defaultPageSize: initialState.pageSize,
        }}
        onTableStateChange={saveTableState}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: t("certification.admin.participants.empty") }}
      />
    </div>
  );
};

export default ParticipantsTab;
